import React, { useEffect, useMemo, useState } from "react";
import {
  Map,
  Crosshair,
  Star,
  TrendingUp,
  Loader2,
  Lock,
  ExternalLink,
  ArrowUpRight,
  ChevronRight,
  Search,
  ClipboardPaste,
  Play,
} from "lucide-react";
import { roleFitGet, isDemo, trackEvent } from "@/lib/api";
import { demoRoleFit } from "@/lib/demoData";
import { getRoles, slugifyRole } from "@/lib/roles";
import { useSearchParams, useNavigate } from "react-router-dom";

function StatBar({ label, value, color = "#E10600" }) {
  return (
    <div>
      <div className="flex items-center justify-between font-mono-body text-[11px] mb-1">
        <span>{label}</span>
        <span className="text-[#0a0a0a]/65">{value}</span>
      </div>
      <div className="h-3 bg-[#0a0a0a]/10 border-2 border-[#0a0a0a]">
        <div
          className="h-full"
          style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: color }}
        />
      </div>
    </div>
  );
}

function SkillList({ title, items, accent }) {
  return (
    <div className="bg-white border-2 border-[#0a0a0a] shadow-brutal-sm p-4">
      <div className="font-accent text-[11px] mb-3" style={{ color: accent }}>
        {title}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items?.length ? (
          items.map((s) => (
            <span
              key={s}
              className="font-mono-body text-[11px] bg-[#FDFBF7] border-2 border-[#0a0a0a] px-2 py-1"
            >
              {s}
            </span>
          ))
        ) : (
          <span className="font-mono-body text-[11px] text-[#0a0a0a]/55">—</span>
        )}
      </div>
    </div>
  );
}

const TEST_URL = (slug) => {
  const qs = new URLSearchParams({ role: slug, start: "1" });
  // Preserve preview-mode demo flag so the embed page stays "authed" in preview.
  if (typeof window !== "undefined") {
    const cur = new URLSearchParams(window.location.search).get("demo");
    if (cur) qs.set("demo", cur);
  }
  return `/dashboard/embed/rolefit?${qs.toString()}`;
};

export default function DashboardRoleFit({ careerPath = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const role = searchParams.get("role") || "sdr";

  const [atlas, setAtlas] = useState(null); // {categories, roles}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [q, setQ] = useState("");
  const [posting, setPosting] = useState("");

  useEffect(() => {
    let cancel = false;
    getRoles().then((a) => {
      if (!cancel) setAtlas(a);
    });
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    // Persist the active role so Simulator embed picks it up by default.
    try {
      if (role) window.localStorage.setItem("jh_last_role_v1", role);
    } catch {
      /* ignore */
    }
    (async () => {
      let res;
      if (isDemo()) res = demoRoleFit(role);
      else res = await roleFitGet(role);
      if (!cancel) {
        setData(res);
        setLoading(false);
        // Fire start once per role load + complete when a score is present.
        if (role) trackEvent("rolefit.start", { role });
        if (res?.ok && typeof res?.score === "number") {
          trackEvent("rolefit.complete", { role, score: res.score });
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, [role]);

  const result = data?.result;
  const isLoggedOut = data?.ok === false;
  const categories = atlas?.categories || [];
  const allRoles = atlas?.roles || [];

  const activeRole = useMemo(
    () => allRoles.find((r) => r.slug === role) || null,
    [allRoles, role]
  );

  const filteredRoles = useMemo(() => {
    let list = allRoles;
    if (filterCat) list = list.filter((r) => r.category === filterCat);
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((r) => {
        const hay = `${r.title} ${(r.alt_titles || []).join(" ")} ${r.slug}`.toLowerCase();
        return hay.includes(needle);
      });
    }
    return list;
  }, [allRoles, filterCat, q]);

  const submitFreeText = (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    // If the search matches an existing role exactly, pick it; else treat as a free-text role.
    const exact = allRoles.find(
      (r) =>
        r.title.toLowerCase() === q.toLowerCase() ||
        r.slug === slugifyRole(q) ||
        (r.alt_titles || []).some((t) => t.toLowerCase() === q.toLowerCase())
    );
    const slug = exact ? exact.slug : slugifyRole(q);
    setSearchParams((p) => {
      p.set("role", slug);
      return p;
    });
  };

  const openPostingTest = () => {
    if (!posting.trim()) return;
    // Open the JobHacki posting-scoring flow in a new tab with the JD prefilled
    // (jobhacki.com persists the text server-side so the slug is sufficient).
    const url = `https://jobhacki.com/rolefit?posting=1`;
    try {
      window.localStorage.setItem("jh_rolefit_posting", posting);
    } catch {
      /* ignore quota */
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid={careerPath ? "dash-career" : "dash-rolefit"} className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          {careerPath ? <Map className="text-[#E10600]" /> : <Crosshair className="text-[#E10600]" />}
          {careerPath ? "CAREER PATH" : "ROLE FIT"}
        </h1>
        <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-2xl">
          {careerPath
            ? "Your competency graph + the next role to target, mapped from your real test scores."
            : `Universal Competency Graph — ${allRoles.length || 127} roles across ${categories.length || 40} categories. Pick one, search any title, or paste a posting.`}
        </p>
      </div>

      {/* Free-text search + paste a posting */}
      <div className="grid grid-cols-12 gap-3">
        <form onSubmit={submitFreeText} className="col-span-12 md:col-span-6 relative">
          <Search
            size={16}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0a0a]/55"
          />
          <input
            data-testid="rolefit-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search 127 roles, or type any title and hit enter…"
            className="w-full bg-white border-2 border-[#0a0a0a] pl-10 pr-4 py-3 font-mono-body text-[13px] shadow-brutal-sm focus:outline-none focus:bg-[#FDFBF7]"
          />
        </form>
        <div className="col-span-12 md:col-span-6 flex items-stretch gap-2">
          <div className="relative flex-1">
            <ClipboardPaste
              size={14}
              strokeWidth={2.5}
              className="absolute left-3 top-3.5 text-[#0a0a0a]/55"
            />
            <input
              data-testid="rolefit-posting"
              value={posting}
              onChange={(e) => setPosting(e.target.value)}
              placeholder="Or paste a job posting URL / text…"
              className="w-full bg-white border-2 border-[#0a0a0a] pl-9 pr-3 py-3 font-mono-body text-[12px] shadow-brutal-sm focus:outline-none"
            />
          </div>
          <button
            onClick={openPostingTest}
            data-testid="rolefit-score-posting"
            className="hover-pop bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-3 font-accent text-[11px] shadow-brutal-sm inline-flex items-center gap-1.5"
          >
            <Play size={11} strokeWidth={3} /> Score it <ExternalLink size={10} strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* Category strip */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="rolefit-categories">
          <button
            onClick={() => setFilterCat("")}
            className={`font-accent text-[11px] border-2 border-[#0a0a0a] px-2.5 py-1.5 ${
              !filterCat ? "bg-[#0a0a0a] text-[#FFC400]" : "bg-white"
            }`}
          >
            ALL · {allRoles.length}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilterCat(c.id)}
              data-testid={`rolefit-cat-${c.id}`}
              className={`font-accent text-[11px] border-2 border-[#0a0a0a] px-2.5 py-1.5 inline-flex items-center gap-1.5 ${
                filterCat === c.id ? "bg-[#0a0a0a] text-[#FFC400]" : "bg-white"
              }`}
              title={c.blurb}
            >
              {c.emoji && <span>{c.emoji}</span>}
              {c.name}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Role list */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3">
          <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal">
            <div className="px-4 py-3 bg-[#0a0a0a] text-[#FFC400] font-accent text-[10px] flex items-center justify-between">
              <span>{filterCat ? categories.find((c) => c.id === filterCat)?.name.toUpperCase() : "ROLE EXPLORER"}</span>
              <span className="text-[#FDFBF7]/55">{filteredRoles.length}</span>
            </div>
            <div className="max-h-[520px] overflow-y-auto">
              {filteredRoles.length === 0 && atlas && (
                <div className="px-4 py-6 font-mono-body text-[12px] text-[#0a0a0a]/55">
                  No roles match. Press enter to test &quot;{q}&quot; as a custom role.
                </div>
              )}
              {filteredRoles.map((r) => (
                <button
                  key={r.slug}
                  onClick={() =>
                    setSearchParams((p) => {
                      p.set("role", r.slug);
                      return p;
                    })
                  }
                  data-testid={`role-${r.slug}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 border-b-2 border-[#0a0a0a]/10 last:border-b-0 hover:bg-[#FDFBF7] ${
                    role === r.slug ? "bg-[#FFC400]/30" : ""
                  }`}
                >
                  <div className="text-left min-w-0">
                    <div className="font-mono-body text-[12px] truncate">{r.title}</div>
                    <div className="font-mono-body text-[10px] text-[#0a0a0a]/55">
                      {r.level && <span className="uppercase mr-1.5">{r.level}</span>}
                      {categories.find((c) => c.id === r.category)?.name || r.category}
                    </div>
                  </div>
                  <ChevronRight size={12} strokeWidth={3} className="text-[#0a0a0a]/55 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="col-span-12 md:col-span-8 lg:col-span-9 space-y-5">
          {loading && (
            <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-8 text-center font-mono-body text-sm text-[#0a0a0a]/65">
              <Loader2 className="mx-auto animate-spin mb-3" /> Loading your scorecard…
            </div>
          )}

          {!loading && (isLoggedOut || !result) && (
            <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6">
              <div className="font-accent text-[11px] text-[#E10600] mb-1">
                {activeRole?.category && (categories.find((c) => c.id === activeRole.category)?.name || activeRole.category)}
              </div>
              <div className="font-display text-2xl mb-1.5">
                {activeRole?.title || role}
              </div>
              {activeRole?.what_it_is && (
                <p className="font-mono-body text-sm text-[#0a0a0a]/75 mb-3">
                  {activeRole.what_it_is}
                </p>
              )}
              {activeRole?.alt_titles?.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {activeRole.alt_titles.slice(0, 6).map((t) => (
                    <span
                      key={t}
                      className="font-mono-body text-[10px] bg-[#FDFBF7] border border-[#0a0a0a]/30 px-1.5 py-0.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {isLoggedOut && <Lock size={14} className="text-[#E10600]" />}
                <a
                  href={TEST_URL(role)}
                  data-testid="rolefit-take-test"
                  className="hover-pop inline-flex items-center gap-2 bg-[#E10600] text-white border-2 border-[#0a0a0a] px-4 py-2.5 font-accent text-sm shadow-brutal-sm"
                >
                  <Play size={12} strokeWidth={3} /> Take the test <ArrowUpRight size={12} strokeWidth={3} />
                </a>
                <span className="font-mono-body text-[11px] text-[#0a0a0a]/55">
                  Opens the assessment inside the dashboard — your score persists here.
                </span>
              </div>
            </div>
          )}

          {!loading && !isLoggedOut && result && (
            <>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-5 bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#0a0a0a] shadow-brutal p-5 relative overflow-hidden">
                  <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
                  <div className="font-accent text-[10px] text-[#FFC400]">SCORE</div>
                  <div className="font-display text-6xl mt-1 leading-none">
                    {result.score}
                    <span className="font-mono-body text-base text-[#FDFBF7]/55">/100</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 font-mono-body text-[12px]">
                    <div>
                      <div className="text-[#FDFBF7]/55 text-[10px]">LEVEL</div>
                      <div className="font-display text-lg">{result.level ?? "—"}</div>
                    </div>
                    <div>
                      <div className="text-[#FDFBF7]/55 text-[10px]">XP</div>
                      <div className="font-display text-lg">{result.xp ?? 0}</div>
                    </div>
                  </div>
                  <a
                    href={TEST_URL(role)}
                    data-testid="rolefit-retest"
                    className="hover-pop mt-4 inline-flex items-center gap-2 bg-[#FFC400] text-[#0a0a0a] border-2 border-[#FFC400] px-3 py-2 font-accent text-[11px]"
                  >
                    Retake <ArrowUpRight size={10} strokeWidth={3} />
                  </a>
                </div>
                <div className="col-span-12 md:col-span-7 bg-white border-4 border-[#0a0a0a] shadow-brutal p-5">
                  <div className="font-accent text-[10px] mb-3 flex items-center gap-1.5">
                    <Star size={11} strokeWidth={3} className="text-[#FFC400]" /> READINESS BREAKDOWN
                  </div>
                  <div className="space-y-3">
                    <StatBar label="Foundation" value={Math.min(100, result.score + 6)} color="#00FF66" />
                    <StatBar label="Depth on must-haves" value={result.score} color="#FFC400" />
                    <StatBar label="Stretch (next level)" value={Math.max(0, result.score - 18)} color="#E10600" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <SkillList title="STRONG" items={result.strong} accent="#00aa44" />
                <SkillList title="WEAK" items={result.weak} accent="#bb8800" />
                <SkillList title="MISSING — STUDY PLAN" items={result.missing} accent="#E10600" />
              </div>

              {careerPath && (
                <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-5">
                  <div className="font-accent text-[11px] mb-3 inline-flex items-center gap-1.5">
                    <TrendingUp size={12} strokeWidth={3} className="text-[#E10600]" /> NEXT ROLE TO TARGET
                  </div>
                  <p className="font-mono-body text-sm text-[#0a0a0a]/80">
                    Based on your current score, the highest-leverage next role is{" "}
                    <b>{activeRole?.title || role} → Senior</b>. Close the missing-skill gap
                    above, then run the test again to lock in the promotion path.
                  </p>
                  <button
                    onClick={() => nav(`/dashboard/jobs?q=${encodeURIComponent(activeRole?.title || role)}`)}
                    className="hover-pop mt-4 inline-flex items-center gap-2 bg-[#E10600] text-white border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm"
                  >
                    See live openings → Job Directory
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
